import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngxs/store';

import { environment } from '@efficiently/client-core-environment';
import { RemoveMessage, Toast } from '@efficiently/client-core-toast';
import { ClickStopDirective } from '@efficiently/client-ui-common';
import { EffIconComponent } from '@efficiently/client-ui-icons';

import { ErrorReferenceLabel } from '../enums/error-reference-label.enum';

const SENTRY_ENDPOINT = 'https://efficiently-dev.sentry.io';

@Component({
	selector: 'ds-toast',
	imports: [EffIconComponent, MatTooltipModule, MatButtonModule, ClickStopDirective],
	templateUrl: './toast.component.html',
	styleUrls: ['./toast.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastComponent {
	private readonly store = inject(Store);
	private readonly clipboard = inject(Clipboard);

	public readonly message: InputSignal<Toast> = input.required();

	protected reloadPending: boolean = false;

	protected readonly errorReferenceLabel = computed(() =>
		this.message()?.eventId ? ErrorReferenceLabel.EventID : ErrorReferenceLabel.RequestID,
	);

	protected readonly errorReferenceId = computed(() => {
		const message = this.message();
		return message.eventId ? message.eventId : message.transactionId;
	});

	protected readonly errorSentryLink = computed(() => {
		if (environment.name === 'Production') return '';

		const eventId = untracked(this.message).eventId;
		return `${SENTRY_ENDPOINT}/issues/?query=${eventId ? 'id' : 'transaction_id'}%3A${this.errorReferenceId()}&statsPeriod=90d`;
	});

	protected copyText(): void {
		this.clipboard.copy(`${this.errorReferenceLabel()}: ${this.errorReferenceId()}`);
	}

	protected handleRefresh(): void {
		window.location.reload();
		this.reloadPending = true;
	}

	protected handleClose(): void {
		this.store.dispatch(new RemoveMessage(this.message().toastId));
	}
}
